import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// --- Components & Styles ---
import AddCustomerModal from "../components/AddCustomerModal";
import "../Styles/ManageCustomers.css";
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL;

const ManageCustomers = () => {
  // --- State ---
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 50;

  // --- Hooks ---
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const debounceTimeout = useRef(null);
  
  // --- Data Fetching ---
  const fetchCustomers = useCallback(
    async (query = "", page = 1) => {
      if (!token) {
        navigate("/login");
        return;
      }
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/customers`, { headers });

        // Local filtering
        const filteredData = res.data.filter(
          (c) =>
            c.customername.toLowerCase().includes(query.toLowerCase()) ||
            c.mobile?.includes(query) ||
            c.city?.toLowerCase().includes(query.toLowerCase())
        );

        // Pagination handled client-side since API doesn’t provide it
        const startIndex = (page - 1) * itemsPerPage;
        const paginated = filteredData.slice(startIndex, startIndex + itemsPerPage);

        setCustomers(paginated);
        setTotalCount(filteredData.length);
        setTotalPages(Math.ceil(filteredData.length / itemsPerPage));
      } catch (err) {
        if (err.response?.status === 401) {
          toast.error("Session expired. Please log in again.");
          navigate("/login");
        } else {
          toast.error("Failed to load customers.");
        }
      } finally {
        setLoading(false);
      }
    },
    [token, navigate, headers]
  );

  useEffect(() => {
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      fetchCustomers(searchTerm, currentPage);
    }, 300);
  }, [fetchCustomers, searchTerm, currentPage]);

  return (
    <>
      <div className="manage-container">
        {/* --- Top Toolbar with Search + Pagination + Add --- */}
        <div className="toolbar">
          {/* Left: Search */}
          <input
            type="text"
            className="search-input"
            placeholder="Search by name, city, or mobile..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />

          {/* Center: Pagination */}
          {!loading && totalCount > 0 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage <= 1}
              >
                ‹
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage >= totalPages}
              >
                ›
              </button>
            </div>
          )}

          {/* Right: Add Customer */}
          <button className="btn-primary" onClick={() => setIsModalOpen(true)}>
            + Add New Customer
          </button>
        </div>

        {/* --- Table --- */}
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>City</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="loading-state">
                    Loading...
                  </td>
                </tr>
              ) : customers.length > 0 ? (
                customers.map((c) => (
                  <tr key={c.customercode}>
                    <td data-label="Code">{c.customercode}</td>
                    <td data-label="Name">{c.customername}</td>
                    <td data-label="Mobile">{c.mobile || "-"}</td>
                    <td data-label="City">{c.city || "-"}</td>
                    <td className="actions-cell" data-label="Actions">
                      <button
                        className="btn btn-primary"
                        onClick={() => navigate(`/masters/customers/view/${c.customercode}`)}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => navigate(`/masters/customers/edit/${c.customercode}`)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="empty-state">
                    No customers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Modal --- */}
      <AddCustomerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          setIsModalOpen(false);
          fetchCustomers(searchTerm, currentPage);
        }}
      />
    </>
  );
};

export default ManageCustomers;
